import { LinearProgress } from '@mui/material';
import { appRoutes } from 'app/app-routes';
import { formValidationSchema } from 'common/functions/form-validation-schema';
import { UserActionCodeValues } from 'common/types/user-action-code';
import { Formik } from 'formik';
import { UserActionCode, useUsersChangePasswordMutation } from 'graphql/generated';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { cacheActions, cacheSelectors } from 'redux/cache.slice';
import { RootState } from 'redux/root-reducer';
import { WsFormTextField } from 'stories/forms/WsFormTextField';
import { WsButton } from 'stories/inputs/WsButton';
import WsFormBox from 'stories/layout/WsFormBox';
import WsFormInfoContainer from 'stories/layout/WsFormInfoContainer';
import WsPanel from 'stories/layout/WsPanel';
import * as yup from 'yup';

type RouteParams = 'id' | 'code';

interface FormValues {
  password: string;
  passwordConfirmation: string;
}

const validationSchema = yup.object().shape({
  password: formValidationSchema.password,
  passwordConfirmation: formValidationSchema.passwordConfirm,
});

const ChangePassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, code } = useParams<Record<RouteParams, string>>();
  const pwCode = useSelector((state: RootState) => cacheSelectors.pwCode(state));

  useEffect(() => {
    if (!pwCode.length || code !== pwCode) navigate(appRoutes.errorPage);
  }, [code, navigate, pwCode]);

  const [changePassword] = useUsersChangePasswordMutation();

  const formAction = async (formValues: FormValues, setSubmitting: (isSubmitting: boolean) => void) => {
    try {
      if (id) {
        setSubmitting(true);
        const data = await changePassword({
          variables: { id, password: formValues.password },
        });
        setSubmitting(false);
        if (!data.data) throw new Error('Register user error');
        const result = data.data.usersChangePassword;
        if (result.code === UserActionCode.passwordChanged && result.user) {
          const { __typename, ...user } = result.user;
          dispatch(cacheActions.user(user));
          navigate(appRoutes.home);
          dispatch(
            cacheActions.addSnack({
              message: UserActionCodeValues[result.code],
              variant: 'success',
            }),
          );
        } else throw new Error(UserActionCodeValues[result.code]);
      }
    } catch (error: any) {
      setSubmitting(false);
      dispatch(
        cacheActions.addSnack({
          message: error.message,
          variant: 'error',
        }),
      );
    }
  };

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={{
        password: '',
        passwordConfirmation: '',
      }}
      onSubmit={(pwValues, { setSubmitting }) => formAction(pwValues, setSubmitting)}
    >
      {props => (
        <form onSubmit={props.handleSubmit}>
          <WsPanel width="340px" title="Change Password">
            <WsFormInfoContainer>
              Passwords must contain at least 8 characters, including at least one uppercase letter, one lowercase
              letter, and one number or special symbol (1,2,3,#,&,%,etc.).
            </WsFormInfoContainer>
            <WsFormBox>
              <WsFormTextField id="password" label="Password" type="password" formik={props} />
              <WsFormTextField id="passwordConfirmation" label="Password Confirmation" type="password" formik={props} />
              {props.isSubmitting && <LinearProgress />}
              <WsButton label="Change Password" type="submit" disabled={props.isSubmitting} />
            </WsFormBox>
          </WsPanel>
        </form>
      )}
    </Formik>
  );
};

export default ChangePassword;
